import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { BulkOperationProgress } from '../typings';
import bulkProgressService from '../services/BulkProgressService';

export interface BulkOperation {
    operationId: string;
    total: number;
    processed: number;
    label: string;
    completed: boolean;
    succeeded: number;
    skipped: number;
    failed: number;
}

interface BulkProgressState {
    operations: Record<string, BulkOperation>;
}

const initialState: BulkProgressState = {
    operations: {}
};

const bulkProgressSlice = createSlice({
    name: 'bulkProgress',
    initialState,
    reducers: {
        operationStarted(state, action: PayloadAction<{ operationId: string; total: number; label: string }>) {
            const { operationId, total, label } = action.payload;
            state.operations[operationId] = {
                operationId, total, processed: 0, label, completed: false, succeeded: 0, skipped: 0, failed: 0
            };
        },
        operationUpdated(state, action: PayloadAction<BulkOperationProgress>) {
            const { operationId, total, processed, label, succeeded, skipped, failed } = action.payload;
            if (state.operations[operationId]) {
                state.operations[operationId].total = total;
                state.operations[operationId].processed = processed;
                state.operations[operationId].label = label;
                // A batch already running when the breakdown shipped has no counters in its hash;
                // the API answers 0 for those, and the caption falls back to the plain count.
                state.operations[operationId].succeeded = succeeded ?? 0;
                state.operations[operationId].skipped = skipped ?? 0;
                state.operations[operationId].failed = failed ?? 0;
                if (total === 0 || processed >= total) {
                    state.operations[operationId].completed = true;
                }
            }
        },
        operationDismissed(state, action: PayloadAction<string>) {
            delete state.operations[action.payload];
        },
        allOperationsCleared(state) {
            state.operations = {};
        }
    },
    extraReducers: (builder) => {
        builder.addCase(REHYDRATE, (state, action: any) => {
            const rehydrated = action.payload?.bulkProgress;
            if (!rehydrated?.operations) return;
            // Keep only incomplete operations from persisted state
            const ops: Record<string, BulkOperation> = {};
            for (const [id, op] of Object.entries(rehydrated.operations) as [string, BulkOperation][]) {
                if (!op.completed) {
                    ops[id] = op;
                }
            }
            state.operations = ops;
        });
    }
});

export const { operationStarted, operationUpdated, operationDismissed, allOperationsCleared } = bulkProgressSlice.actions;

export const subscribeToBulkProgress = (operationId: string) => {
    return async (dispatch: any) => {
        await bulkProgressService.streamProgress(operationId, (data) => {
            dispatch(operationUpdated(data));
        });
    };
};

export default bulkProgressSlice.reducer;
